import { cloneDeep } from 'lodash';

import type { Category, Item, Schema } from '../../../models';
import type { FuelUserAccess } from '../../../models/FuelUserAccess';
import type { OU } from '../../../models/Ou';
import clientUtils from '../../../utils/clientUtils';
import itemUtils from '../../../utils/itemUtils';
import type { AssetForm } from '../../forms';
import { SchemaType } from '../../forms/Asset-Editor/SchemaTypeOptions';
import { createAssetSkeleton } from './createAssetSkeleton';

export const toSaveAssetRequest: (
  form: AssetForm,
  schemas: Array<Schema>,
  categories: Array<Category>,
  fuelUserAccessData: FuelUserAccess,
  ouData: OU
) => Item = (form, schemas, categories, fuelUserAccessData, ouData) => {
  // console.log('toSaveAssetRequest', { form });

  const buildPayloadBase: () => Item = () => {
    let obj: Item;
    // console.log('buildPayloadBase', form)
    if (form.originalAssetData === null) {
      obj = createAssetSkeleton(form.schemaType, fuelUserAccessData, ouData);
      // these are being dropped, but the new portal backend doesn't like the default values of null coming from the above function...set to false instead...
      if (obj.data.businessMetadata) {
        obj.data.businessMetadata.embargoDateOverride = false;
        obj.data.businessMetadata.expireDateOverride = false;
      }
      obj.uiSaveFlags = {};
    } else {
      obj = cloneDeep(form.originalAssetData);
      obj.uiSaveFlags = {};

      // aalt 2023-10-18 #448947 Validate Feed-Ingested Ad Markers On Ingest -- code to cleans the timeStampMarkers
      if (obj.data.timeStampMarkers && obj.data.timeStampMarkers.length > 0) {
        const adInsertionPointsIndex = _.findIndex(obj.data.timeStampMarkers, (x) => x.markerType === 'AdInsertionPoints');
        if (adInsertionPointsIndex > -1) {
          const timeStampObj = obj.data.timeStampMarkers[adInsertionPointsIndex];
          if (timeStampObj.timeStamps && timeStampObj.timeStamps.length > 0) {
            const assetDuration = obj.duration != null && +obj.duration > 0 ? +obj.duration : null;
            // aalt 2023-10-18 #448947 cannot have clip markers less than 0 or greater than clip duration
            const cleansedTimestamps = itemUtils.cleanseTimeStamps(timeStampObj.timeStamps, 0, assetDuration);
            // console.log('toSaveAssetRequest', { timeStamps: timeStampObj.timeStamps, cleansedTimestamps });
            if (cleansedTimestamps && cleansedTimestamps.length !== timeStampObj.timeStamps.length) {
              obj.data.timeStampMarkers[adInsertionPointsIndex].timeStamps = cleansedTimestamps;
              obj.uiSaveFlags.hasTimeStampMarkersChange = true;
            }
          }
        }
      }
    }
    return obj;
  };

  const payload = buildPayloadBase();

  payload.ouId = ouData.id;
  payload.data.originalFileName = form.originalFileName;
  payload.data.businessMetadata.title = form.title.trim();
  payload.data.businessMetadata.automatedTitle = form.automatedTitle;
  payload.data.businessMetadata.synopsis = form.synopsis;
  payload.data.businessMetadata.publicationDate = form.publicationDate
    ? clientUtils.formatDateTimeToUTC(form.publicationDate)
    : form.publicationDate;
  payload.applyAutomatedTransitions = form.applyAutomatedTransitions;
  payload.schemaType = form.schemaType;
  payload.data.doNotDelete = form.doNotDelete;
  payload.data.common = form.common;
  payload.data.accessSettings = form.accessSettings;
  payload.data.linkedAssets = form.linkedAssets;

  if (form.schemaType === SchemaType.Image) {
    // Clear out the Video related data
    payload.categories = form.categories;
    payload.tags = form.tags;

    payload.attributes = [];
    payload.zipcode = null;
    payload.data.businessMetadata.embargoDateOverride = true;
    payload.data.businessMetadata.embargoDate = null;
    payload.data.businessMetadata.expireDateOverride = true;
    payload.data.businessMetadata.expireDate = null;
    payload.applyAutomatedTransitions = false;
  } else if (form.schemaType === SchemaType.Graphics) {
    // Clear out the Video related data
    payload.categories = form.categories;
    payload.tags = form.tags;
    payload.attributes = [];
    payload.zipcode = null;
    payload.data.businessMetadata.embargoDateOverride = true;
    payload.data.businessMetadata.embargoDate = null;
    payload.data.businessMetadata.expireDateOverride = true;
    payload.data.businessMetadata.expireDate = null;
    payload.applyAutomatedTransitions = false;
  } else {
    payload.categories = form.categories;
    payload.tags = form.tags;

    payload.data.businessMetadata.embargoDate = form.embargoDate ? clientUtils.formatDateTimeToUTC(form.embargoDate) : null;

    payload.data.businessMetadata.expireDate = form.expireDate ? clientUtils.formatDateTimeToUTC(form.expireDate) : null;
    payload.data.businessMetadata.subTitle = form.subTitle;
  }

  const buildCustomFields: (form: AssetForm) => Item = (form) => {
    return form.customFields;
  };

  payload.data.businessMetadata.customFields = buildCustomFields(form);
  return payload;
};
